import { DocumentDownloadLink, DocumentDownloadButton } from "components/elements/DocumentDownload";
import { ArrowDownTrayIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import EyeIcon from "@heroicons/react/24/solid/EyeIcon";
import Typography, { ITypo } from "components/elements/Typography";
import classes from "./classes.module.scss";
import { matchPath, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";

import { useCallback, useEffect, useState } from "react";

import { container } from "tsyringe";
import ProductSubscribedService from "services/ProductSubscribedService";
import { EProductDocumentType } from "common/enums/ProductSheet/DocumentsTemplates/EProductDocumentType";
import Table from "components/elements/Table";
import Button, { EButtonVariant } from "components/elements/Button";
import I18n from "components/materials/I18n";
import { IRowProps, TableColumn } from "components/elements/Table/MuiTable";
import ProductSubscribedResponseResource from "common/resources/ProductSubscribed/ProductSubscribedResponseResource";
import Form from "components/materials/Form";
import Modal from "components/elements/Modal";

import { ISubscriptionOutletContext } from "..";
import MessageBox from "components/elements/MessageBox";
import useDocumentFinalization from "components/pages/Subscriptions/shared/useDocumentFinalization";
import DownloadCompleteDossierButton from "components/pages/Subscriptions/shared/DownloadCompleteDossierButton";
import { EProductType } from "common/enums/ProductSheet/EProductType";

const productSubscribedService = container.resolve(ProductSubscribedService);

export default function ValidateProductDocuments() {
	const { productSubscribedId } = useParams();
	const [docTypes, setDocTypes] = useState<EProductDocumentType[]>([]);
	const [productSubscribed, setProductSubscribed] = useState<ProductSubscribedResponseResource | null>(null);
	const { documentValidationSteps, loadSubscription, subscription } = useOutletContext<ISubscriptionOutletContext>();
	const { reviewDocuments, dialog, busy } = useDocumentFinalization(subscription.id, documentValidationSteps, false);

	const navigate = useNavigate();
	const location = useLocation();

	const [isReportModalOpen, setIsReportModalOpen] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const currentStepIndex = documentValidationSteps.findIndex((step) => matchPath(step.path, location.pathname));
	const nextStep = documentValidationSteps[currentStepIndex + 1];

	useEffect(() => {
		if (!productSubscribedId) return;
		productSubscribedService.getByUid(productSubscribedId).then((productSubscribed) => {
			setProductSubscribed(productSubscribed);
		});
		productSubscribedService.getSignableDocumentsTypes(productSubscribedId).then((docTypes) => setDocTypes(docTypes));
	}, [productSubscribedId]);

	const handleValidateStep = useCallback(async () => {
		if (!productSubscribedId) return;

		try {
			await productSubscribedService.validateDocuments(productSubscribedId);
			loadSubscription();
			if (nextStep) {
				navigate(nextStep.path);
			} else {
				await reviewDocuments();
			}
		} catch (error) {
			console.error("Erreur lors de la validation :", error);
		}
	}, [productSubscribedId, loadSubscription, nextStep, navigate, reviewDocuments]);

	const handleSubmitError = useCallback(async () => {
		if (!productSubscribedId || !errorMessage.trim()) return;

		try {
			await productSubscribedService.reportAnomaly(productSubscribedId, {
				message: errorMessage.trim(),
			});
			loadSubscription();
			setIsReportModalOpen(false);
			setErrorMessage("");

			if (nextStep) {
				navigate(nextStep.path);
			} else {
				await reviewDocuments();
			}
		} catch (err) {
			console.error("Erreur lors de la soumission :", err);
		}
	}, [productSubscribedId, errorMessage, loadSubscription, nextStep, navigate, reviewDocuments]);

	if (!productSubscribedId || !productSubscribed) return null;

	return (
		<div className={classes["root"]}>
			<Typography typo={ITypo.P_MEDIUM} className={classes["subtitle"]}>
				Vous pouvez dès-à-présent vérifier les documents avant leur envoi en signature électronique.
			</Typography>
			<div>
				<div className={classes["root"]}>
					<div className={classes["header"]}>
						<Typography typo={ITypo.P_LARGE_BOLD}>{productSubscribed.productSheet?.name}</Typography>
						{/* Private equity : le dossier part en relecture chez la société de gestion avant signature, pièces comprises. */}
						{productSubscribed.productSheet?.product?.productType === EProductType.private_equity ? (
							<DownloadCompleteDossierButton productSubscribed={productSubscribed} />
						) : (
							<DocumentDownloadButton url={productSubscribedService.buildSignableDocumentsZipUrl(productSubscribedId)} icon={<ArrowDownTrayIcon />}>
								{I18n.asset.pages.product.download_all_documents}
							</DocumentDownloadButton>
						)}
					</div>
					<Table columnsHead={columsHead} rows={buildRows(docTypes, productSubscribedId)} onNext={null} searchbarDisabled={true} />
				</div>
			</div>

			<div className={classes["footer"]}>
				<Button disabled={busy} onClick={handleValidateStep}>
					{I18n.asset.common.validate}
				</Button>
				<Button icon={<ExclamationTriangleIcon />} iconposition="left" variant={EButtonVariant.OUTLINED} onClick={() => setIsReportModalOpen(true)}>
					{I18n.asset.common.report_anomaly}
				</Button>
			</div>

			<Modal isOpen={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} className={classes["modal-content"]}>
				<Typography typo={ITypo.H1} className={classes["modal-title"]}>
					{I18n.asset.pages.subscriptions.report_error_modal.title}
				</Typography>
				<Form className={classes["modal-form"]} onSubmit={(e) => e.preventDefault()}>
					<textarea
						className={classes["modal-textarea"]}
						name="errorMessage"
						placeholder={I18n.asset.pages.subscriptions.report_error_modal.placeholder}
						value={errorMessage}
						onChange={(e) => setErrorMessage(e.target.value)}
					/>
					<MessageBox className={classes["message-content"]} type="info" text={I18n.asset.pages.subscriptions.report_error_modal.message} />

					<div className={classes["modal-button"]}>
						<Button onClick={() => setIsReportModalOpen(false)} variant={EButtonVariant.OUTLINED}>
							{I18n.asset.common.cancel}
						</Button>
						<Button onClick={handleSubmitError}>{I18n.asset.common.send}</Button>
					</div>
				</Form>
			</Modal>
			{dialog}
		</div>
	);
}

function buildRows(docTypes: EProductDocumentType[], productSubscribedId: string): IRowProps[] {
	const productDocuments = docTypes.map((docType) => {
		return {
			rowKey: docType,
			name: I18n.asset.enums.EProductDocumentType[docType],
			action: getAction(docType, productSubscribedId),
		};
	});

	return [...productDocuments];
}

function getAction(docType: EProductDocumentType, productSubscribedId: string) {
	return (
		<div className={classes["actions"]}>
			<DocumentDownloadLink href={productSubscribedService.buildSignableDocumentUrl(productSubscribedId, docType)}>
				<EyeIcon />
			</DocumentDownloadLink>
			<DocumentDownloadLink href={productSubscribedService.buildSignableDocumentUrl(productSubscribedId, docType)} download={`${docType}.pdf`}>
				<ArrowDownTrayIcon />
			</DocumentDownloadLink>
		</div>
	);
}

const columsHead: readonly TableColumn[] = [
	{
		headContent: I18n.asset.pages.product.documents_table.name,
		key: "name",
	},
	{
		headContent: "",
		key: "action",
	},
];
